/**
 * Экран «Холдинг» (AK-898 режим «Холдинг», AK-903 таблица хозяйств; макет v2
 * экран 4, approve founder 08.10 «по смыслу, компоненты штатные DS»).
 *
 * Уровень компании = только общая аналитика + вход в хозяйство (В-2): плитки
 * (хозяйств, пашня учётная, полей, «двойник заполнен N из M»), структура
 * культур и севооборот как прежде, ниже — таблица хозяйств с заполненностью
 * двойника и «что не заполнено». Сквозных списков «все поля компании» нет —
 * в данные идут через хозяйство.
 *
 * Тот же экран в режиме кластера (роль кластера, в переключателе кластер) —
 * «сводка кластера»: заголовок «Кластер «…»», ручки ограничивают выборку
 * линзой сами (Head-решение по #1109, вопрос 2).
 *
 * Плитки и подзаголовок берут цифры ТОЛЬКО из `totals` ручки
 * `/dashboard/farms-twin` без поиска — тот же источник, что у таблицы (счётчик
 * и строки не разъедутся), и считает он выборку, а не страницу; до ответа —
 * Skeleton. `/dashboard/summary` остаётся ради `has_company` (увод в онбординг)
 * и плашки ошибки при смене области — его цифры под подписи плиток не
 * подходят (контурная сумма ≠ учётная пашня, `fields` ≠ `fields_active`).
 *
 * Пока `/scope/available` грузится, вместо шапки и плиток — Skeleton, а не
 * «Холдинг», который через кадр станет «Кластером» (follow-up из #1109).
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BarChart3 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { OverdueSowingsBanner } from '@/components/OverdueSowingsBanner';
import { ErrorState } from '@/components/ErrorState';
import { useScopeMode } from '@/components/scope/useScopeMode';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tile, TileGrid } from '@/components/ui/tile-grid';
import { CropsBarCard } from '@/pages/dashboard/CropsBarCard';
import { FarmsTwinTable } from '@/pages/dashboard/FarmsTwinTable';
import { RotationCard } from '@/pages/dashboard/RotationCard';
import {
  DEFAULT_FARMS_TWIN_PARAMS,
  farmsTwinKeys,
  formatHa,
  useFarmsTwin,
} from '@/pages/dashboard/farmsTwin';
import { api } from '@/lib/api';
import { classifyApiError } from '@/lib/apiError';
import { pluralRu } from '@/lib/dateFmt';
import { useScopeChanged } from '@/lib/scopeEvent';

interface Me {
  user: { first_name?: string };
  active_tenant?: { name: string };
}

interface Summary {
  companies: number;
  clusters: number;
  farms: number;
  brigades: number;
  fields: number;
  total_area_ha: number;
  has_company: boolean;
}

function HeaderSkeleton() {
  return (
    <div data-testid="dashboard-skeleton">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="mt-2 h-4 w-40" />
      <TileGrid cols={4} className="mt-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </TileGrid>
    </div>
  );
}

export function DashboardPage() {
  const qc = useQueryClient();
  const scope = useScopeMode();
  const [me, setMe] = useState<Me | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  // Ошибка стартовой загрузки — блокирующая: без summary дашборд рисовать
  // нечем. Раньше catch клал только Sentry, `loading` уходил в false, а
  // `summary` оставался null → экран навсегда висел на «Загружаем…» (AK-790
  // re-land #670, вердикт head-web P1-1). Теперь плашка + «Повторить».
  const [initError, setInitError] = useState<string | null>(null);
  // Ошибка refetch при смене хозяйства — самая опасная точка (P1-2): ошибка
  // глоталась, и на экране оставались цифры ПРЕЖНЕГО хозяйства как свои.
  // Держим отдельно от `summary`: плашка ложится ПОВЕРХ старых цифр (человек
  // видит, что данные стухли), но сами цифры не сбрасываем — иначе мигало бы
  // на каждом переключении.
  const [refetchError, setRefetchError] = useState<string | null>(null);

  const loadInit = useCallback(async () => {
    setLoading(true);
    setInitError(null);
    try {
      const [meData, summaryData] = await Promise.all([
        api.get<Me>('/api/v1/auth/me'),
        api.get<Summary>('/api/v1/dashboard/summary'),
      ]);
      setMe(meData);
      setSummary(summaryData);
      if (!summaryData.has_company) {
        window.location.href = '/onboarding/company';
        return;
      }
    } catch (err) {
      setInitError(classifyApiError(err, 'dashboard.init').message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInit();
  }, [loadInit]);

  const refetchSummary = useCallback(() => {
    setRefetchError(null);
    api
      .get<Summary>('/api/v1/dashboard/summary')
      .then(setSummary)
      .catch((err) => {
        setRefetchError(classifyApiError(err, 'dashboard.refetchSummary').message);
      });
    // Линза сменилась — выборка хозяйств другая: плитки и таблица
    // перечитываются одним ключом.
    void qc.invalidateQueries({ queryKey: farmsTwinKeys.all });
  }, [qc]);
  useScopeChanged(refetchSummary);

  // Плитки — из той же ручки, что таблица, без поиска (поиск — дело таблицы).
  const twin = useFarmsTwin(DEFAULT_FARMS_TWIN_PARAMS, !!summary);
  const totals = twin.data?.totals;

  if (loading && !summary) {
    return (
      <AppShell>
        <div className="max-w-[1200px] text-muted-foreground">Загружаем…</div>
      </AppShell>
    );
  }

  if (!summary) {
    return (
      <AppShell>
        <div className="max-w-[1200px]">
          <ErrorState
            message={initError ?? 'Не удалось загрузить дашборд.'}
            onRetry={() => void loadInit()}
          />
        </div>
      </AppShell>
    );
  }

  const firstName = me?.user.first_name ?? 'коллега';
  const tenantName = me?.active_tenant?.name ?? 'ваша компания';
  const isCluster = scope.mode === 'cluster' && scope.current?.scope_type === 'cluster';
  const title = isCluster ? `Кластер «${scope.current?.name ?? ''}»` : `Холдинг «${tenantName}»`;

  // Все цифры шапки и плиток — ТОЛЬКО из `totals` (ревью head-web #1119):
  // `summary.total_area_ha` — сумма всех полей с контуром, не учётная пашня,
  // `summary.fields` ≠ `fields_active`; подставлять их под чужими подписями
  // значило бы показать число, которое через 300 мс сменится другим.
  // До ответа — Skeleton; `summary` здесь только ради `has_company`.
  const clustersCount = totals?.clusters ?? 0;
  const wait = <Skeleton className="inline-block h-6 w-16 align-middle" />;

  return (
    <AppShell>
      {/* pb-safe-fab — место под плавающий ФАБ «ИИ» + safe-area, чтобы кнопка
          не перекрывала последний контент (легенду графика) на дашборде.
          Адресно на контентной странице, НЕ в общем main (иначе ломает карту)
          — AK-374. */}
      <div className="pb-safe-fab">
        <OverdueSowingsBanner />

        {scope.status === 'loading' ? (
          <HeaderSkeleton />
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
                <p className="mt-1 text-muted-foreground">
                  Добрый день, {firstName}!
                  {totals && (
                    <>
                      {' · '}
                      {totals.farms} {pluralRu(totals.farms, ['хозяйство', 'хозяйства', 'хозяйств'])}
                      {!isCluster && (
                        <>
                          {' · '}
                          {clustersCount} {pluralRu(clustersCount, ['кластер', 'кластера', 'кластеров'])}
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
              {/* AK-898 [умолч. 13]: «Аналитика» — не пункт меню. Вход в аналитику
                  прошлых лет — одной строкой отсюда; по прямой ссылке
                  `/analytics/history` открывается как раньше. */}
              <Button asChild variant="outline" size="sm">
                <Link to="/analytics/history">
                  <BarChart3 className="h-4 w-4" />
                  Аналитика прошлых лет
                </Link>
              </Button>
            </div>

            {refetchError && (
              // ПОВЕРХ старых цифр, не ВМЕСТО: цифры прежнего хозяйства не
              // выдаются за свежие молча, но и не пропадают экраном «нет данных».
              <div className="mt-4">
                <ErrorState variant="inline" message={refetchError} onRetry={refetchSummary} />
              </div>
            )}

            <TileGrid cols={4} className="mt-4" data-testid="dashboard-tiles">
              <Tile
                size="lg"
                label="Хозяйств"
                value={totals ? totals.farms : wait}
                hint={
                  !totals
                    ? 'считаем…'
                    : isCluster
                      ? 'в кластере'
                      : `в ${clustersCount} ${pluralRu(clustersCount, ['кластере', 'кластерах', 'кластерах'])}`
                }
              />
              <Tile
                size="lg"
                label="Пашня, учётная"
                value={totals ? formatHa(totals.arable_ha) : wait}
                hint="активные поля с контуром"
              />
              <Tile
                size="lg"
                label="Полей"
                value={totals ? totals.fields_active.toLocaleString('ru-RU') : wait}
                hint="активных"
              />
              <Tile
                size="lg"
                label="Двойник заполнен полностью"
                value={totals ? `${totals.farms_complete} из ${totals.farms}` : wait}
                hint={totals ? `хозяйств · ${totals.total_steps} из ${totals.total_steps} шагов` : 'считаем…'}
              />
            </TileGrid>
          </>
        )}

        <CropsBarCard />
        <RotationCard />
        <FarmsTwinTable />
      </div>
    </AppShell>
  );
}

export default DashboardPage;
